import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MimeAttachmentContent,
  attachmentContentToBlob,
  getAttachmentPreviewKind,
} from "./emailAttachmentUtils";

const PREVIEW_HEIGHT_STORAGE_KEY = "xgs-email-attachment-preview-height";
const DEFAULT_PREVIEW_HEIGHT = 360;
const MIN_PREVIEW_HEIGHT = 160;
const MAX_PREVIEW_HEIGHT = 900;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const loadStoredPreviewHeight = (): number => {
  if (typeof window === "undefined") return DEFAULT_PREVIEW_HEIGHT;
  try {
    const raw = localStorage.getItem(PREVIEW_HEIGHT_STORAGE_KEY);
    if (!raw) return DEFAULT_PREVIEW_HEIGHT;
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return DEFAULT_PREVIEW_HEIGHT;
    return clamp(parsed, MIN_PREVIEW_HEIGHT, MAX_PREVIEW_HEIGHT);
  } catch {
    return DEFAULT_PREVIEW_HEIGHT;
  }
};

export interface EmailAttachmentPreviewProps {
  attachment: MimeAttachmentContent;
  onClose: () => void;
  showPreview?: boolean;
}

const EmailAttachmentPreview: React.FC<EmailAttachmentPreviewProps> = ({ attachment, onClose, showPreview = true }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [previewHeight, setPreviewHeight] = useState(loadStoredPreviewHeight);
  const resizeStart = useRef<{ y: number; height: number } | null>(null);
  const previewKind = getAttachmentPreviewKind(attachment.mimeType, attachment.filename);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    const load = async () => {
      try {
        let blob: Blob;
        if (attachment.url) {
          const res = await fetch(attachment.url);
          const rawBlob = await res.blob();
          blob =
            !attachment.mimeType || rawBlob.type === attachment.mimeType
              ? rawBlob
              : new Blob([rawBlob], { type: attachment.mimeType });
        } else if (attachment.content != null) {
          blob = attachmentContentToBlob(attachment);
        } else {
          return;
        }

        if (cancelled) return;

        url = URL.createObjectURL(blob);
        setObjectUrl(url);

        if (previewKind === "text") {
          blob.text().then((text) => {
            if (!cancelled) setTextContent(text);
          }).catch(() => {
            if (!cancelled) setTextContent(null);
          });
        }
      } catch {
        if (!cancelled) {
          setObjectUrl(null);
          setTextContent(null);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setObjectUrl(null);
      setTextContent(null);
    };
  }, [attachment, previewKind]);

  const persistHeight = useCallback((height: number) => {
    const next = clamp(height, MIN_PREVIEW_HEIGHT, MAX_PREVIEW_HEIGHT);
    setPreviewHeight(next);
    try {
      localStorage.setItem(PREVIEW_HEIGHT_STORAGE_KEY, String(next));
    } catch {
      // ignore
    }
  }, []);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      if (e.button !== 0) return;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      resizeStart.current = { y: e.clientY, height: previewHeight };
    },
    [previewHeight]
  );

  const onResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (!resizeStart.current) return;
    const dy = e.clientY - resizeStart.current.y;
    persistHeight(resizeStart.current.height + dy);
  }, [persistHeight]);

  const onResizePointerUp = useCallback((e: React.PointerEvent) => {
    if (!resizeStart.current) return;
    resizeStart.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }, []);

  return (
    <div className="xgs-email-viewer__attachment-preview">
      <div className="xgs-email-viewer__attachment-preview-header">
        <span className="xgs-email-viewer__attachment-preview-title">{attachment.filename}</span>
        <div className="xgs-email-viewer__attachment-preview-header-actions">
          <span className="xgs-email-viewer__attachment-preview-height-label">
            Height: {previewHeight.toFixed(0)}px
          </span>
          {showPreview && (<button
            type="button"
            className="xgs-email-viewer__attachment-preview-close"
            onClick={onClose}
          >
            Close preview
          </button>
          )}
        </div>
      </div>
      <div
        className="xgs-email-viewer__attachment-preview-body"
        style={{ height: previewHeight }}
      >
        {previewKind === "image" && objectUrl && (
          <img
            src={objectUrl}
            alt={attachment.filename}
            className="xgs-email-viewer__attachment-preview-image"
          />
        )}
        {previewKind === "pdf" && objectUrl && (
          <iframe
            title={attachment.filename}
            src={objectUrl}
            className="xgs-email-viewer__attachment-preview-iframe"
          />
        )}
        {previewKind === "text" && textContent != null && (
          <pre className="xgs-email-viewer__attachment-preview-text">{textContent}</pre>
        )}
        {previewKind === "text" && textContent == null && objectUrl && (
          <iframe
            title={attachment.filename}
            src={objectUrl}
            className="xgs-email-viewer__attachment-preview-iframe"
          />
        )}
      </div>
      <div
        className="xgs-email-viewer__attachment-preview-resize"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize attachment preview"
      >
        Drag to resize preview
      </div>
    </div>
  );
};

export default EmailAttachmentPreview;
